import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    google: typeof google;
    initGooglePlaces?: () => void;
    gm_authFailure?: () => void;
  }
}

interface AddressInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

let googleScriptLoaded = false;
let googleScriptLoading = false;
let googleScriptError = false;
const loadCallbacks: ((error?: boolean) => void)[] = [];

const loadGooglePlacesScript = (apiKey: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (googleScriptLoaded) {
      resolve(!googleScriptError);
      return;
    }

    if (googleScriptError) {
      resolve(false);
      return;
    }

    loadCallbacks.push((error) => resolve(!error));

    if (googleScriptLoading) {
      return;
    }

    googleScriptLoading = true;

    // Handle authentication failures
    window.gm_authFailure = () => {
      googleScriptError = true;
      googleScriptLoading = false;
      loadCallbacks.forEach((cb) => cb(true));
      loadCallbacks.length = 0;
    };

    // Create callback function
    window.initGooglePlaces = () => {
      googleScriptLoaded = true;
      googleScriptLoading = false;
      loadCallbacks.forEach((cb) => cb(false));
      loadCallbacks.length = 0;
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGooglePlaces`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      googleScriptError = true;
      googleScriptLoading = false;
      loadCallbacks.forEach((cb) => cb(true));
      loadCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
};

export const AddressInput = ({
  value,
  onChange,
  placeholder = "Enter address...",
  className,
  disabled = false,
}: AddressInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

  const initAutocomplete = useCallback(() => {
    if (
      !inputRef.current ||
      !window.google?.maps?.places ||
      autocompleteRef.current
    )
      return;

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["address"],
          componentRestrictions: { country: "us" },
          fields: ["formatted_address", "address_components"],
        },
      );

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current?.getPlace();
        if (place?.formatted_address) {
          onChange(place.formatted_address);
        }
      });
    } catch {
      setHasError(true);
    }
  }, [onChange]);

  useEffect(() => {
    if (!apiKey) {
      return;
    }

    setIsLoading(true);
    loadGooglePlacesScript(apiKey).then((success) => {
      setIsLoading(false);
      if (success) {
        initAutocomplete();
      } else {
        setHasError(true);
      }
    });
  }, [apiKey, initAutocomplete]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className={cn("relative", className)}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : hasError ? (
          <AlertCircle className="h-4 w-4 text-amber-500" />
        ) : (
          <MapPin className="h-4 w-4" />
        )}
      </div>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        className="pl-9"
      />
      {!apiKey && (
        <p className="text-xs text-muted-foreground mt-1">
          Configure VITE_GOOGLE_PLACES_API_KEY for address autocomplete
        </p>
      )}
      {hasError && apiKey && (
        <p className="text-xs text-amber-600 mt-1">
          Places API error - check API key configuration in Google Cloud Console
        </p>
      )}
    </div>
  );
};
